import React from 'react';

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Stack } from '@liscio/ui';

import { CreateAccountSectionProps } from './sectionTypes';
import { LabeledField, SubsectionTitle } from '../CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from '../createAccountValidationSchema';
import { emptyBankAccountRow } from '../entityFormDefaults';

export const CreateAccountBankAccountsFields: React.FC<
  CreateAccountSectionProps
> = ({ layout = 'responsive' }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'bankAccounts',
  });

  const fieldBreakpoint = layout === 'single-column' ? 12 : 4;
  const bankAccountErrors = errors.bankAccounts;

  return (
    <Stack spacing={2}>
      <SubsectionTitle>Bank Account Details</SubsectionTitle>
      {fields.map((field, index) => {
        const rowErrors = Array.isArray(bankAccountErrors)
          ? bankAccountErrors[index]
          : undefined;

        return (
          <Stack key={field.id} spacing={2}>
            {fields.length > 1 && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <SubsectionTitle sx={{ mb: 0 }}>
                  {`${index + 1}. Bank Account`}
                </SubsectionTitle>
                <Button
                  type="button"
                  size="small"
                  color="inherit"
                  onClick={() => remove(index)}
                >
                  Remove
                </Button>
              </Stack>
            )}

            <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
              <Grid item xs={12} sm={fieldBreakpoint}>
                <LabeledField label="Bank Name">
                  <TextField
                    {...register(`bankAccounts.${index}.bank_name`)}
                    fullWidth
                    error={Boolean(rowErrors?.bank_name)}
                    helperText={rowErrors?.bank_name?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={fieldBreakpoint}>
                <LabeledField label="Routing #">
                  <TextField
                    {...register(`bankAccounts.${index}.routing_number`)}
                    fullWidth
                    error={Boolean(rowErrors?.routing_number)}
                    helperText={rowErrors?.routing_number?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={fieldBreakpoint}>
                <LabeledField label="Account #">
                  <TextField
                    {...register(`bankAccounts.${index}.encryptedAccountNumber`)}
                    fullWidth
                    type="password"
                    error={Boolean(rowErrors?.encryptedAccountNumber)}
                    helperText={rowErrors?.encryptedAccountNumber?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={fieldBreakpoint}>
                <LabeledField label="Account type">
                  <TextField
                    {...register(`bankAccounts.${index}.account_type`)}
                    fullWidth
                    error={Boolean(rowErrors?.account_type)}
                    helperText={rowErrors?.account_type?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={fieldBreakpoint}>
                <LabeledField label="Website">
                  <TextField
                    {...register(`bankAccounts.${index}.web_site`)}
                    fullWidth
                    error={Boolean(rowErrors?.web_site)}
                    helperText={rowErrors?.web_site?.message}
                    autoComplete="off"
                  />
                </LabeledField>
              </Grid>

              <Grid item xs={12} sm={fieldBreakpoint}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register(`bankAccounts.${index}.is_ach_autopay`)}
                    />
                  }
                  label="Use this account for monthly ACH auto payments?"
                />
              </Grid>

              <Grid item xs={12}>
                <LabeledField label="Notes">
                  <TextField
                    {...register(`bankAccounts.${index}.notes`)}
                    fullWidth
                    multiline
                    minRows={2}
                    error={Boolean(rowErrors?.notes)}
                    helperText={rowErrors?.notes?.message}
                  />
                </LabeledField>
              </Grid>
            </Grid>
          </Stack>
        );
      })}

      <Button
        type="button"
        variant="text"
        onClick={() => append({ ...emptyBankAccountRow })}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        + Add another bank account
      </Button>
    </Stack>
  );
};

export default CreateAccountBankAccountsFields;
